import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  IonList,
  IonItem,
  IonActionSheet,
  IonLabel,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonLoading,
} from '@ionic/react';
import { openOutline, closeOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { PageLayout } from 'containers/layout';
import { VenueLogo } from 'components/venue';
import AppActions, { AppSelectors } from 'redux/AppRedux';
import PayActions, { PaySelectors } from 'redux/PayRedux';
import AuthActions, { AuthSelectors } from 'redux/AuthRedux';
import OrderActions from 'redux/OrderRedux';
import transformVenue from 'transform/venue';
import { BackgroundMode } from '@awesome-cordova-plugins/background-mode';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions';
import { getDistance } from 'utils/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import PaymentErrorModal from 'containers/payment/PaymentErrorModal';
import api_v2 from 'services/api_v2';
import { isAndroidVersion, isMobileVersion, isWebVersion } from 'utils/platform';
import LocationPermissionModal from '../containers/tab/LocationPermissionModal';

function LocationsPage() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [active, setActive] = useState(null);
  const [hasLocationPermission, setLocationPermission] = useState(null);
  const [showPaymentErrorAlert, setShowPaymentErrorAlert] = useState(false);
  const [keyword, setKeyword] = useState('');

  const venues = useSelector(AppSelectors.selectVenues);
  const currentLocation = useSelector(AppSelectors.selectGeoLocation);
  const token = useSelector(AuthSelectors.selectToken);
  const ticket = useSelector(PaySelectors.selectTicket);
  const currentVenue = useSelector(AppSelectors.selectVenue);
  const cardId = useSelector(PaySelectors.selectCurrentMethod);
  const loadingVenues = useSelector(AppSelectors.selectLoadingVenues);

  const filterSortedVenues = useMemo(() => {
    const filterVenues = venues.filter(item => item?.name?.toLowerCase().indexOf(keyword?.toLowerCase()) !== -1)

    if (!currentLocation.lat) {
      return filterVenues;
    }

    return filterVenues.sort((a, b) => {
      const d1 = getDistance(
        a.location.lat,
        a.location.lng,
        currentLocation.lat,
        currentLocation.lng
      );
      const d2 = getDistance(
        b.location.lat,
        b.location.lng,
        currentLocation.lat,
        currentLocation.lng
      );

      return d1 - d2;
    });
  }, [venues, currentLocation, keyword]);

  const configurePushNotification = () => {
    if (!isMobileVersion || isWebVersion) {
      return;
    }

    console.log('push notification configuration.');

    // Request permission to use push notifications
    // iOS will prompt user and return if they granted permission or not
    // Android will just grant without prompting
    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        console.log('Push Notification permission granted');
        PushNotifications.register();
      } else {
        console.log('Push Notification permission denied');
        // Show some error
      }
    });

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (fcmToken) => {
      console.log('Push registration success, token: ', fcmToken.value);

      dispatch(PayActions.setRegistToken(fcmToken.value));
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error) => {
      console.log('Error on registration: ', JSON.stringify(error));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        console.log('Push received: ', JSON.stringify(notification));

        if (isAndroidVersion) {
          LocalNotifications.schedule({
            notifications: [
              {
                id: 1,
                title: notification.title ?? '',
                body: notification.body ?? '',
                schedule: { at: new Date(Date.now() + 1000 * 1) },
                extra: notification.data,
              },
            ],
          });
        }
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification) => {
        console.log('Push action performed: ', JSON.stringify(notification));
      }
    );
  };

  useEffect(() => {
    // Subscribe to 'activate' event of BgMode
    if (isAndroidVersion) {
      BackgroundMode.on('activate').subscribe(() => {
        console.log('BackgroundMode activated!!!');
        BackgroundMode.disableWebViewOptimizations();
        BackgroundMode.disableBatteryOptimizations();
      });

      BackgroundMode.on('deactivate').subscribe(() => {
        console.log('BackgroundMode deactivated!!!');
      });

      AndroidPermissions.checkPermission(
        AndroidPermissions.PERMISSION.ACCESS_COARSE_LOCATION
      ).then((result) => {
        console.log('Has Geo Location permission?', JSON.stringify(result));
        if (!result.hasPermission) {
          setLocationPermission(true);
        } else {
          dispatch(AuthActions.getGeoCurrentLocation());
        }
      });
    } else {
      dispatch(AuthActions.getGeoCurrentLocation());
    }

    configurePushNotification();

    return () => {
      if (isAndroidVersion) {
        BackgroundMode.un('activate');
        BackgroundMode.un('deactivate');
      }
    };
  }, []);

  const onContinueAction = () => {
    dispatch(AuthActions.getGeoCurrentLocation());
    setLocationPermission(null);
  };

  const handleLoadVenues = async (e) => {
    const api = api_v2(token);

    try {
      const resp = await api.getVenues();
      dispatch(AppActions.setVenues(resp.map((v) => transformVenue(v))));
    } catch (err) {
      console.log("load venues error: ", err.message);
    }

    e.detail.complete();
  };

  const validateVenue = (loc) => {
    if (ticket && ticket.venueId !== loc.id) {
      dispatch(
        AppActions.setToast(
          'You already have a tab opened. Please close before opening new one.',
          'danger'
        )
      );

      return false;
    }

    return true;
  };

  const getCardPreAuthorization = async () => {
    try {
      const api = api_v2(token);
      await api.preauthorizeCard(cardId);
      return true;
    } catch (error) {
      setShowPaymentErrorAlert(true);
      return false;
    }
  };

  const handleChangeValue = (e) => {
    setKeyword(e.target.value);
  };

  const buttons = [];

  if (active) {
    if (active.viewMenuEnabled) {
      buttons.push({
        text: 'View Menu',
        icon: openOutline,
        handler: () => {
          history.push(`/locations/${active.id}/view-menu`);
        },
      });
    }

    if (active.pickupEnabled) {
      buttons.push({
        text: 'Order for pickup (counter, curbside, etc)',
        icon: openOutline,
        handler: async () => {
          const isValidVenue = validateVenue(active);

          if (isValidVenue) {
            const isCardPreAuthorization = await getCardPreAuthorization();

            if (isCardPreAuthorization) {
              dispatch(PayActions.setIsTable(false));

              if (ticket) {
                history.push(`/locations/${active.id}/tab`);
              } else {
                dispatch(OrderActions.initializeCart(active.id));
              }
            }
          }
        },
      });
    }

    if (active.openTabEnabled !== false) {
      buttons.push({
        text: active.tableSeatingEnabled
          ? 'Are you ordering from the bar?'
          : 'Open Tab',
        icon: openOutline,
        handler: async () => {
          const isValidVenue = validateVenue(active);

          if (isValidVenue) {
            const isCardPreAuthorization = await getCardPreAuthorization();

            if (isCardPreAuthorization) {
              dispatch(PayActions.setIsTable(false));
              dispatch(PayActions.createTicket(active.id));
            }
          }
        },
      });
    }

    if (active.tableSeatingEnabled) {
      buttons.push({
        text: 'Are you ordering from a table?',
        icon: openOutline,
        handler: async () => {
          const isValidVenue = validateVenue(active);

          if (isValidVenue) {
            history.push(`/locations/${active.id}/table-number`);
          }
        },
      });
    }
  }

  return (
    <PageLayout title="Locations">
      <IonRefresher slot="fixed" onIonRefresh={handleLoadVenues}>
        <IonRefresherContent />
      </IonRefresher>

      {
        loadingVenues
          ? <IonLoading isOpen message="Loading..." /> :
          <IonList>
            <IonItem lines="none" className="mt mb">
              <IonSearchbar
                className="searchbar-input"
                onIonChange={handleChangeValue}
                value={keyword}
              />
            </IonItem>

            {filterSortedVenues.map((loc) => (
              <IonItem
                key={loc.id}
                button
                color={currentVenue === loc.id ? 'light' : undefined}
                onClick={() => setActive(loc)}
              >
                <VenueLogo logo={loc.logo} />
                <IonLabel>
                  <h2>{loc.name}</h2>
                  <p>{loc.address}</p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
      }

      <LocationPermissionModal
        open={!!hasLocationPermission}
        onClose={() => onContinueAction()}
      />

      <IonActionSheet
        header="Actions"
        isOpen={!!active}
        onDidDismiss={() => setActive(null)}
        buttons={[
          ...buttons,
          {
            text: 'Cancel',
            icon: closeOutline,
            role: 'cancel',
          },
        ]}
      />

      <PaymentErrorModal
        isOpen={showPaymentErrorAlert}
        setShowAlert={setShowPaymentErrorAlert}
      />
    </PageLayout>
  );
}

export default LocationsPage;
